import { Signal } from '@angular/core';
import { EntityType, Stages } from '../constants';
import { BannerType } from '@wcg/frontend-framework';
import { Study } from '@data-access/studies';
import { UserRole } from '@data-access/roles';
import { WorkspaceReview } from '@submissions/v1/models';
import { OpenDialogState } from '@data-access/shared';
import { SubmissionOrganizationStatus } from '../constants/submission-detail.constant';

/** Organization slots referenced by GET /organization-validation lists */
export type OrganizationAssignmentRole = 'sponsor' | 'pi' | 'facility_owner';

/** GET body uses string slots; normalize to OrganizationAssignmentRole in the store */
export type OrganizationValidationResponse = {
  status: Exclude<SubmissionOrganizationStatus, 'checking'>;
  assigned_organization_types: string[];
  unassigned_organization_types: string[];
};

export type UpdateSubmissionStagePayload = {
  workspaceId: string;
  entityType: EntityType;
  organizationValidationData?: AssignOrganizationtData;
  unassignedOrganizationTypes?: OrganizationAssignmentRole[];
};

export type SubmissionDetailState = {
  canCloseDialog: boolean;
  error: Error | null;
  isLoading: boolean;
  hasOutcomeDocuments: boolean;
  submission: SubmissionResponse;
  sites: SubmissionSitesListResponse;
  /** Path for appeal submission setup navigation, set by rxMethod then cleared after use */
  appealSubmissionSetupPath: string | null;
  isSubmissionCancelled: boolean;
  organiationStatus: SubmissionOrganizationStatus;
  /** From GET organization-validation; already-linked org slots */
  organizationValidationAssignedTypes: OrganizationAssignmentRole[];
  /** From GET organization-validation; slots the user must assign in the modal */
  organizationValidationUnassignedTypes: OrganizationAssignmentRole[];
  /** Flag to trigger refetch of organization assignment data after changes */
  reloadContactOrgs: boolean;
};

export enum SUBMISSION_TYPE_ENUM_PARENT {
  modificationToResearch = 'Modification to Research',
}

import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  SubmissionSite,
  SubmissionSitesListResponse,
} from '@data-access/sites';
/**
 * ERM-12600 audit evidence for a resubmit. Recorded, not enforced — the
 * backend resubmits either way. The lock acknowledgement is UI-only.
 */
export type ResubmitPayload = {
  /** External to-dos still open at the moment the user confirmed. */
  open_todo_count: number;
};

export type SubmissionDetailSignalState = {
  loadSubmission: (submissionId: string) => void;
  loadSubmissionSites: (submissionId: string, queryParams?: HttpParams) => void;
  loadOutcomeDocuments: (submissionId: string) => void;
  handleCancelSubmissionRequest: (
    dialogData: OpenDialogState,
    submissionId: string,
  ) => void;
  openDialog: (payload: OpenDialogState) => any;
  resetState: (submissionId?: string) => void;
  deleteSubmission: (
    workspaceId: string,
    reason_for_delete: string,
    isInternal: boolean,
  ) => Observable<unknown>;
  /** ERM-12647 — external user's explicit Re-submit: stamps `resubmitted_at`, re-locks and notifies WCG, regardless of open to-do count */
  resubmit: (workspaceId: string, payload: ResubmitPayload) => void;
  errorHandler: (errorMessage: string) => void;
  updateSubmission: (submission: SubmissionResponse) => void;
  markSubmissionOpenForEdits: (expectedSubmissionId?: string) => void;
  updateSubmissionName: (workspaceId: string, value: string) => void;
  updateSubmissionStage: (payload: UpdateSubmissionStagePayload) => void;
  /** Update submission sites data and count */
  updateSubmissionSites: (data: SubmissionSite[], total: number) => void;
  /** Complete IBC submission */
  completeIbcSubmission: (workspaceId: string) => void;
  /** Trigger loading appeal submission setup path (path set in $appealSubmissionSetupPath; caller navigates then clears) */
  openAppealSubmissionSetup: (submissionId: string) => void;
  /** Clear the appeal submission setup path after navigation */
  clearAppealSubmissionSetupPath: () => void;

  /// Check if organization is assigned to the submission, which is required before moving to review stage. This is needed to be checked before review stage transition, as the assigned organization can be changed while the submission is in progress.
  organizationValidation: (workspaceId: string) => void;

  /** The current submission */
  $submission: Signal<SubmissionResponse | null>;
  $sites: Signal<SubmissionSitesListResponse>;
  /** Path to navigate for appeal submission setup, set by openAppealSubmissionSetup */
  $appealSubmissionSetupPath: Signal<string | null>;
  /** The ID of the current submission */
  $submissionId: Signal<string | null>;
  /** The current stage of the submission */
  $current_stage: Signal<string | null>;
  /** Loading state of the submission */
  $isLoading: Signal<boolean>;
  /** Error state of the submission */
  $error: Signal<Error | null>;
  /** Whether the submission has outcome documents */
  $hasOutcomeDocuments: Signal<boolean>;
  /** Whether the submission is cancelled */
  $isSubmissionCancelled: Signal<boolean>;

  $organiationStatus: Signal<SubmissionOrganizationStatus>;
  $reloadContactOrgs: Signal<boolean>;
  $organizationValidationAssignedTypes: Signal<OrganizationAssignmentRole[]>;
  $organizationValidationUnassignedTypes: Signal<OrganizationAssignmentRole[]>;
};

export type FailedDocument = {
  id: string;
  title: string;
};

export type FailedDocumentProps = {
  bannerType: BannerType;
  closeButton: boolean;
  failedDocuments: FailedDocument[];
  handleBannerClose: () => void;
  hasFailedDocuments: boolean;
  message: string;
};

export type SubmissionStageUserData = {
  /** User ID */
  id: string;
  /** User name */
  name: string;
};

export type SubmissionStage = {
  /** Stage name */
  name: Stages;
  /** Stage start date */
  start_date: string;
  /** Stage end date */
  end_date: string;
  /** Whether this is the current stage */
  current_stage: boolean;
  /** Order of the stage in the workflow */
  stage_order: number;
  /** User data associated with the stage */
  user_data?: SubmissionStageUserData;
  /** Turnaround time for the stage */
  tat?: number;
};

export type SubmissionQcStatus = {
  status: boolean;
  data: {
    sme_qc_marks: number;
    documents: number;
    todos: number;
  };
};

export type SubmissionTypeParent = {
  id: string;
  name: string;
};
export type SubmissionType = {
  id: string;
  name: string;
  data: Record<string, unknown>;
  parent?: SubmissionTypeParent;
  review_board?: 'irb' | 'ibc';
  int_id?: number;
  origin?: string;
};

export type SubmissionResponse = {
  created_dt: string;
  created_by: string;
  current_stage: Stages;
  data: any;
  entity_id: string;
  entity_type: EntityType;
  id: string;
  int_id: number;
  has_mock_study: boolean;
  name: string;
  pi_legacy_id?: number;
  reviews: WorkspaceReview[];
  site?: {
    id: string;
    cro: {
      name: string;
    };
    name: string;
  };
  submitted_by_name?: string;
  submitted_date?: string;
  created_by_name: string;
  site_int_id?: number;
  status: string;
  study?: Study;
  type_id: string;
  update_dt: string;
  updated_by: any;
  stages: {
    [key: string]: SubmissionStage;
  };
  sponsor?: {
    id: string;
    name: string;
  };
  priority: string;
  type: SubmissionType;
  on_hold?: unknown;
  origin: string | null;
  package_id: string | null;
  cnx3_parent_submission_id: number | null;
  partner: string | null;
  partner_display: string | null;
  project_id: number | null;
  preparing_for_board_review_date: string;
  board_review_date: string;
  role: UserRole;
  qc_status?: SubmissionQcStatus;
  sme_user_attribute?: boolean;
  qc_user_attribute?: boolean;
  sme?: any;
  qc?: any;
  sites?: {
    data?: 'all' | 'None' | string[];
    investigator?: unknown;
  };
  created_by_user?: any;
  site_ids?: string[];
  site_id?: string;
  is_multisite?: boolean;
  sites_data?: unknown[];
  review_location?: {
    id: string;
    name: string;
  };

  similar_studies?: {
    id: string;
    int_id: number;
  }[];

  /** Parent submission ID if this is an appeal */
  parent_submission_id?: string | null;
  /** Original submission ID in the appeal chain */
  original_submission_id?: string | null;

  permissions?: {
    can_view_submission_details?: boolean;
    can_view_submission_documents?: boolean;
  };
  version?: number;
  /**
   * The AIIRB review slug linked to this workspace (v3/AI-enabled only). Absent if the
   * workspace isn't AI-enabled, or if the AIIRB linkage failed on create (best-effort on
   * the backend -- a v3 workspace can still come back with this unset).
   */
  aiirb_review_slug?: string;
  /**
   * Whether the submission documents are unlocked as a whole (ERM-12395).
   * True lets the external user add a new submission document and makes the
   * existing ones removable/replaceable; Submit re-locks it. Absent means
   * locked — only the unlock endpoint sets it, and legacy v1 never has it.
   **/
  documents_unlocked_as_whole?: boolean;
  /**
   * Whether the questionnaire is open for the client to edit (ERM-13220),
   * which is what the header's OPEN FOR EDITS badge shows. Set while the
   * submission has a customer-facing to-do awaiting external action; the
   * client's resubmit closes it. Always false while collab_hub_redesign
   * is off, and absent from legacy responses.
   **/
  open_for_edits?: boolean;
  /**
   * ERM-12647 — the single source of truth for "the external user has
   * resubmitted". Stamped by Re-submit, which also re-locks and notifies WCG.
   * Read this, never the open-to-do count: clearing every to-do by hand drops
   * that count to zero without a resubmit. Absent/null means not yet.
   **/
  resubmitted_at?: string | null;
  /**
   * A count of the number of appeals associated to this workspace.
   * This value should only take into account appeals the user has access to
   **/
  num_appeals?: number;
  /** Appeal deadline: This value should only take into account appeals the user has access to  **/
  appeal_deadline_date?: string;
};
export type UploadState = 'pending' | 'uploading' | 'completed' | 'failed';

export type CancelSubmission = {
  message: string;
};

export type AssignOrganizationtDialogData = {
  dialogTitle: string;
  dialogSize: string;
  unassignedOrganizationTypes: OrganizationAssignmentRole[];
  assignedOrganizationTypes?: OrganizationAssignmentRole[];
  dialogDescription?: string;
  dialogBanner?: string;
  dialogNote?: string;
};

export type AssignOrganizationtData = {
  sponsor_organization_id?: string;
  pi_organization_id?: string;
  facility_owner_organization_id?: string;
};

export type OrganizationValidationInput = {
  skip_organization_check?: boolean;
} & AssignOrganizationtData;

export type AssignOrganizationFormModel = {
  [key in keyof AssignOrganizationtData]: AssignOrganizationtData[key] | null;
};

export type AssignOrganizationField =
  | 'sponsor_organization_id'
  | 'pi_organization_id'
  | 'facility_owner_organization_id';
